import { ButtonBaseProps } from '@mui/material';
import { forwardRef, MouseEvent, ReactNode } from 'react';
import { PillLabel, PillOverlay, PillRoot, PillTrailing, PillVariant } from './pill.elements';

export type { PillVariant };

export interface PillHoverAction {
  icon: ReactNode;
  label: string;
  onClick: (event: MouseEvent<HTMLElement>) => void;
}

export interface PillProps extends Omit<ButtonBaseProps, 'children'> {
  label: string;
  icon?: ReactNode;
  variant?: PillVariant;
  /** Fades out while the hover action overlay is shown. */
  trailing?: ReactNode;
  /** Trailing control revealed on hover/focus without resizing the pill. */
  hoverAction?: PillHoverAction;
  /** Renders the pill as a link: ButtonBase switches its root to an anchor when set. */
  href?: string;
  target?: string;
  rel?: string;
  /** Wraps a long label over several lines instead of truncating it at a fixed width. */
  wrapLabel?: boolean;
}

/**
 * Compact rounded pill for inline lists in the side panel (participants, multi-select
 * values). The hover action lives inside the pill's own button, so it is a span that
 * stops its click from reaching the pill; keyboard users reach the same action through
 * whatever menu owns the list.
 */
export const Pill = forwardRef<HTMLButtonElement, PillProps>(function Pill(
  {
    label,
    icon,
    variant = 'solid',
    trailing,
    hoverAction,
    wrapLabel = false,
    onClick,
    ...rest
  },
  ref,
) {
  return (
    <PillRoot
      ref={ref}
      variant={variant}
      hasOverlay={Boolean(hoverAction)}
      hasAction={Boolean(onClick || rest.href)}
      hasIcon={Boolean(icon)}
      wrapLabel={wrapLabel}
      onClick={onClick}
      {...rest}
    >
      {icon}
      <PillLabel className="pill-label">{label}</PillLabel>
      {trailing ? <PillTrailing className="pill-trailing">{trailing}</PillTrailing> : null}
      {hoverAction ? (
        <PillOverlay
          variant={variant}
          className="pill-overlay"
          aria-hidden
          data-testid="pill-hover-action"
          title={hoverAction.label}
          onClick={(event: MouseEvent<HTMLElement>) => {
            // inside a link pill the overlay click would otherwise also follow the href
            event.preventDefault();
            event.stopPropagation();
            hoverAction.onClick(event);
          }}
        >
          {hoverAction.icon}
        </PillOverlay>
      ) : null}
    </PillRoot>
  );
});
